import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import { mockBillSummaries } from './mockData';
import SearchFiltersComponent from './components/SearchFilters';
import type { BillSummary, SearchFilters } from './types';

export default function BillSummaryPage() {
  const navigate = useNavigate();
  const [summaries] = useState<BillSummary[]>(mockBillSummaries);
  const [filteredSummaries, setFilteredSummaries] = useState<BillSummary[]>(mockBillSummaries);

  const handleSearch = (filters: SearchFilters) => {
    let filtered = [...summaries];

    if (filters.searchValue) {
      filtered = filtered.filter((summary) => {
        const value = filters.searchValue.toLowerCase();
        switch (filters.searchType) {
          case 'tenantId':
            return summary.tenantId.toLowerCase().includes(value);
          case 'tenantName':
            return summary.tenantName.toLowerCase().includes(value);
          case 'userId':
            return summary.userId.toLowerCase().includes(value);
          case 'userName':
            return summary.userName.toLowerCase().includes(value);
          default:
            return true;
        }
      });
    }

    if (filters.startMonth) {
      filtered = filtered.filter((summary) => summary.billMonth >= filters.startMonth);
    }

    if (filters.endMonth) {
      filtered = filtered.filter((summary) => summary.billMonth <= filters.endMonth);
    }

    if (filters.status) {
      filtered = filtered.filter((summary) => summary.status === filters.status);
    }

    setFilteredSummaries(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '出账中':
        return 'text-blue-600 bg-blue-50';
      case '已出账':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleViewDetails = (summary: BillSummary) => {
    navigate(`/billing/details?month=${summary.billMonth}&tenantId=${summary.tenantId}`);
  };

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">账单汇总</h1>
        <p className="text-sm text-gray-500 mt-1">查看租户的月度账单汇总信息</p>
      </div>

      <SearchFiltersComponent onSearch={handleSearch} />

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  账单月份
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  租户ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  租户名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  状态
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  总金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  总积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  优惠金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  优惠积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  实付金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  实付积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSummaries.map((summary) => (
                <tr key={summary.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {summary.billMonth}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{summary.tenantId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{summary.tenantName}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{summary.userId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{summary.userName}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(summary.status)}`}>
                      {summary.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {summary.totalAmount > 0 ? `¥${summary.totalAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {summary.totalPoints > 0 ? summary.totalPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {summary.discountAmount > 0 ? `¥${summary.discountAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {summary.discountPoints > 0 ? summary.discountPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-teal-600 whitespace-nowrap">
                    {summary.actualAmount > 0 ? `¥${summary.actualAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-teal-600 whitespace-nowrap">
                    {summary.actualPoints > 0 ? summary.actualPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <button
                      onClick={() => handleViewDetails(summary)}
                      className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                    >
                      账单明细
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredSummaries.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-300"></i>
            <p className="mt-2 text-sm text-gray-500">暂无账单汇总</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
